import { CSSProperties, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import LockIcon from '@mui/icons-material/Lock';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import { useAppSelector } from 'hooks';
import { getAuthToken } from 'utils/auth';
import { clearBiometricCredentials } from 'utils/clearBiometricCredentials';
import { APP_DOMAIN } from 'utils/urls';

type ChangePasswordModalProps = {
  open: boolean;
  onClose: () => void;
};

type ChangePasswordForm = {
  oldPassword: string;
  newPassword: string;
  confirmPassword: string;
};

function getBackofficeBase(): string {
  return `https://backoffice.${APP_DOMAIN}/api`;
}

const initialForm: ChangePasswordForm = {
  oldPassword: '',
  newPassword: '',
  confirmPassword: ''
};

// These MUST mirror the backoffice validator (DescopeController::validatePasswordChange):
// min:8 + upper + lower + digit + a special character (regex /[\W_]/). Omitting the
// special-character rule let a password like "NewPass1" pass here but get rejected
// server-side as "Invalid input" — the reported bug.
const passwordRuleChecks = (value: string) => ({
  length: value.length >= 8,
  uppercase: /[A-Z]/.test(value),
  lowercase: /[a-z]/.test(value),
  number: /[0-9]/.test(value),
  special: /[\W_]/.test(value)
});

const ChangePasswordModal = ({ open, onClose }: ChangePasswordModalProps) => {
  const user = useAppSelector((state) => state.user);
  const [form, setForm] = useState<ChangePasswordForm>(initialForm);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // Per-field show/hide (mask/unmask) state — matches the Unity password fields.
  const [reveal, setReveal] = useState<Record<keyof ChangePasswordForm, boolean>>({
    oldPassword: false,
    newPassword: false,
    confirmPassword: false
  });
  const toggleReveal = (field: keyof ChangePasswordForm) =>
    setReveal((prev) => ({ ...prev, [field]: !prev[field] }));

  useEffect(() => {
    if (!open) {
      setForm(initialForm);
      setError('');
      setSuccess('');
      setSubmitting(false);
      // Re-mask every field when the modal closes so a reopen never reveals a
      // previously-unmasked password.
      setReveal({ oldPassword: false, newPassword: false, confirmPassword: false });
    }
  }, [open]);

  const validate = () => {
    if (!form.oldPassword || !form.newPassword || !form.confirmPassword) {
      return 'Please fill in all password fields.';
    }
    if (!Object.values(passwordRuleChecks(form.newPassword)).every(Boolean)) {
      return 'Your password must have at least 8 characters, 1 uppercase letter, 1 lowercase letter, 1 number, and 1 special character.';
    }
    if (form.newPassword !== form.confirmPassword) {
      return 'New password and confirm password do not match.';
    }
    return '';
  };

  const handleChange = (field: keyof ChangePasswordForm, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setError('');
    setSuccess('');
  };

  const handleClose = () => {
    if (!submitting) {
      onClose();
    }
  };

  const handleSubmit = async () => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    const { access_token, refresh_token } = getAuthToken();
    if (!user.playerEmail || !user.playerGUID || !refresh_token) {
      setError(
        'Your session is missing required authentication details. Please log in again and retry.'
      );
      return;
    }

    setSubmitting(true);
    setError('');
    setSuccess('');

    try {
      const body = new FormData();
      body.append('loginId', user.playerEmail);
      body.append('oldPassword', form.oldPassword);
      body.append('newPassword', form.newPassword);
      body.append('confirmPassword', form.confirmPassword);
      body.append('refreshJwt', refresh_token);
      body.append('GUID', user.playerGUID);

      const response = await fetch(`${getBackofficeBase()}/descope/changePassword`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${access_token}`
        },
        body
      });

      const json = await response.json().catch(() => ({}));
      const data = json?.data ?? {};
      const message =
        data?.message ||
        data?.details?.errorDescription ||
        json?.message ||
        'Unable to change password right now.';

      if (!response.ok || json?.success === false || data?.error) {
        setError(message);
        return;
      }

      // The password change itself already succeeded server-side by this
      // point — report that now, unconditionally, rather than after the
      // biometric clear below. A failure clearing the credential is a
      // separate, non-fatal problem; it must never surface as "password
      // change failed" (which it didn't) or push the player to retry the
      // one action that's already done and can't be retried usefully here.
      setSuccess(message || 'Password updated successfully.');
      setForm(initialForm);

      // EN-5415: a biometric-bound credential is meant to unlock the account
      // this password protects — leaving it usable past a password change
      // would let biometrics alone outlive the very reset that was supposed
      // to lock a compromised or forgotten password out. Its own try/catch
      // keeps that failure from being reported as (or masking) the password
      // change outcome above.
      try {
        await clearBiometricCredentials(user.playerEmail);
      } catch (clearError) {
        console.error(
          '[Profile] Failed to clear biometric credentials after password change:',
          clearError
        );
        setError(
          "Password changed, but we couldn't confirm biometric sign-in was cleared on this device. Turn it off in Settings to be safe."
        );
      }
    } catch (submitError) {
      console.error('[Profile] Failed to change password:', submitError);
      setError('Unable to change password right now. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) {
    return null;
  }

  return createPortal(
    <div style={backdropStyle} onClick={handleClose}>
      <div style={modalStyle} onClick={(event) => event.stopPropagation()}>
        <div style={headerStyle}>
          <div style={headerTitleWrapStyle}>
            <span style={headerTitleStyle}>Change Password</span>
            <span style={headerSubtitleStyle}>Update your login password</span>
          </div>
          <button
            type='button'
            onClick={handleClose}
            style={closeButtonStyle}
            disabled={submitting}>
            <CloseIcon style={{ fontSize: '1rem' }} />
          </button>
        </div>

        <div style={containerStyle}>
          <div style={introCardStyle}>
            <div style={introIconWrapStyle}>
              <LockIcon style={{ fontSize: '1.2rem', color: '#E98C26' }} />
            </div>
            <div style={introTextWrapStyle}>
              <span style={introTitleStyle}>Keep your account secure</span>
              <span style={introSubtitleStyle}>
                Use your current password first, then enter a stronger replacement.
              </span>
            </div>
          </div>

          <div style={fieldGroupStyle}>
            <label style={fieldLabelStyle} htmlFor='current-password'>
              Current Password
            </label>
            <div style={inputWrapStyle}>
              <VpnKeyIcon style={inputIconStyle} />
              <input
                id='current-password'
                type={reveal.oldPassword ? 'text' : 'password'}
                value={form.oldPassword}
                onChange={(event) => handleChange('oldPassword', event.target.value)}
                style={inputStyle}
                placeholder='Enter current password'
                autoComplete='current-password'
              />
              <RevealButton
                shown={reveal.oldPassword}
                onToggle={() => toggleReveal('oldPassword')}
              />
            </div>
          </div>

          <div style={fieldGroupStyle}>
            <label style={fieldLabelStyle} htmlFor='new-password'>
              New Password
            </label>
            <div style={inputWrapStyle}>
              <LockIcon style={inputIconStyle} />
              <input
                id='new-password'
                type={reveal.newPassword ? 'text' : 'password'}
                value={form.newPassword}
                onChange={(event) => handleChange('newPassword', event.target.value)}
                style={inputStyle}
                placeholder='Enter new password'
                autoComplete='new-password'
              />
              <RevealButton
                shown={reveal.newPassword}
                onToggle={() => toggleReveal('newPassword')}
              />
            </div>
          </div>

          <div style={rulesCardStyle}>
            <span style={rulesTitleStyle}>Your password must have at least:</span>
            <div style={rulesListStyle}>
              <PasswordRule
                ok={passwordRuleChecks(form.newPassword).length}
                text='At least 8 characters'
              />
              <PasswordRule
                ok={passwordRuleChecks(form.newPassword).uppercase}
                text='1 uppercase letter'
              />
              <PasswordRule
                ok={passwordRuleChecks(form.newPassword).lowercase}
                text='1 lowercase letter'
              />
              <PasswordRule ok={passwordRuleChecks(form.newPassword).number} text='1 number' />
              <PasswordRule
                ok={passwordRuleChecks(form.newPassword).special}
                text='1 special character'
              />
            </div>
          </div>

          <div style={fieldGroupStyle}>
            <label style={fieldLabelStyle} htmlFor='confirm-password'>
              Confirm New Password
            </label>
            <div style={inputWrapStyle}>
              <CheckCircleIcon style={inputIconStyle} />
              <input
                id='confirm-password'
                type={reveal.confirmPassword ? 'text' : 'password'}
                value={form.confirmPassword}
                onChange={(event) => handleChange('confirmPassword', event.target.value)}
                style={inputStyle}
                placeholder='Re-enter new password'
                autoComplete='new-password'
              />
              <RevealButton
                shown={reveal.confirmPassword}
                onToggle={() => toggleReveal('confirmPassword')}
              />
            </div>
          </div>

          {error && (
            <div style={errorBannerStyle}>
              <ErrorOutlineIcon style={{ fontSize: '1rem', color: '#ff7b72' }} />
              <span>{error}</span>
            </div>
          )}

          {success && (
            <div style={successBannerStyle}>
              <CheckCircleIcon style={{ fontSize: '1rem', color: '#6ddf8b' }} />
              <span>{success}</span>
            </div>
          )}

          <div style={btnRowStyle}>
            <button
              type='button'
              onClick={handleClose}
              style={secondaryBtnStyle}
              disabled={submitting}>
              Cancel
            </button>
            <button
              type='button'
              onClick={handleSubmit}
              style={submitting ? disabledPrimaryBtnStyle : primaryBtnStyle}
              disabled={submitting}>
              {submitting ? 'Updating...' : 'Change Password'}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

// Show/Hide (mask/unmask) eye toggle rendered at the end of each password field.
const RevealButton = ({ shown, onToggle }: { shown: boolean; onToggle: () => void }) => (
  <button
    type='button'
    onClick={onToggle}
    style={revealBtnStyle}
    aria-label={shown ? 'Hide password' : 'Show password'}
    aria-pressed={shown}>
    {shown ? (
      <VisibilityOffIcon style={revealIconStyle} />
    ) : (
      <VisibilityIcon style={revealIconStyle} />
    )}
  </button>
);

const PasswordRule = ({ ok, text }: { ok: boolean; text: string }) => (
  <div style={ruleRowStyle}>
    <CheckCircleIcon
      style={{
        fontSize: '0.9rem',
        color: ok ? '#6ddf8b' : 'rgba(255,255,255,0.2)'
      }}
    />
    <span style={{ ...ruleTextStyle, color: ok ? '#ffffff' : 'rgba(255,255,255,0.55)' }}>
      {text}
    </span>
  </div>
);

export default ChangePasswordModal;

const containerStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.9rem',
  padding: '1.1rem 1.1rem 1.2rem'
};

const backdropStyle: CSSProperties = {
  position: 'fixed',
  inset: 0,
  zIndex: 1200,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '1rem',
  background: 'rgba(0, 0, 0, 0.72)',
  backdropFilter: 'blur(6px)'
};

const modalStyle: CSSProperties = {
  width: '100%',
  maxWidth: '480px',
  borderRadius: '18px',
  border: '1px solid rgba(255,255,255,0.08)',
  background: 'linear-gradient(180deg, rgba(14, 20, 31, 0.98), rgba(9, 14, 24, 0.98))',
  boxShadow: '0 20px 60px rgba(0,0,0,0.45)',
  overflow: 'hidden'
};

const headerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '1rem 1.1rem',
  borderBottom: '1px solid rgba(255,255,255,0.08)'
};

const headerTitleWrapStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.15rem'
};

const headerTitleStyle: CSSProperties = {
  fontSize: '1rem',
  fontWeight: 700,
  color: '#ffffff'
};

const headerSubtitleStyle: CSSProperties = {
  fontSize: '0.74rem',
  color: 'rgba(255,255,255,0.55)'
};

const closeButtonStyle: CSSProperties = {
  width: '2.1rem',
  height: '2.1rem',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '10px',
  border: 'none',
  cursor: 'pointer',
  color: 'rgba(255,255,255,0.65)',
  background: 'rgba(255,255,255,0.05)'
};

const introCardStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.9rem',
  padding: '0.95rem 1rem',
  borderRadius: '14px',
  border: '1px solid rgba(233, 140, 38, 0.2)',
  background: 'linear-gradient(180deg, rgba(233, 140, 38, 0.08), rgba(255, 255, 255, 0.02))'
};

const introIconWrapStyle: CSSProperties = {
  width: '2.5rem',
  height: '2.5rem',
  borderRadius: '999px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'rgba(233, 140, 38, 0.12)',
  border: '1px solid rgba(233, 140, 38, 0.16)'
};

const introTextWrapStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.15rem'
};

const introTitleStyle: CSSProperties = {
  fontSize: '0.95rem',
  fontWeight: 700,
  color: '#ffffff'
};

const introSubtitleStyle: CSSProperties = {
  fontSize: '0.72rem',
  lineHeight: 1.4,
  color: 'rgba(255,255,255,0.55)'
};

const fieldGroupStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.4rem'
};

const fieldLabelStyle: CSSProperties = {
  fontSize: '0.72rem',
  fontWeight: 700,
  color: 'rgba(255,255,255,0.55)',
  textTransform: 'uppercase',
  letterSpacing: '0.04em'
};

const inputWrapStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.6rem',
  borderRadius: '12px',
  border: '1px solid rgba(255,255,255,0.08)',
  background: 'rgba(255,255,255,0.04)',
  padding: '0 0.85rem'
};

const inputIconStyle: CSSProperties = {
  fontSize: '1rem',
  color: 'rgba(233, 140, 38, 0.8)'
};

const inputStyle: CSSProperties = {
  width: '100%',
  border: 'none',
  outline: 'none',
  background: 'transparent',
  color: '#ffffff',
  fontSize: '0.92rem',
  padding: '0.9rem 0',
  fontFamily: 'inherit'
};

const revealBtnStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: 'none',
  background: 'transparent',
  color: 'rgba(255,255,255,0.55)',
  cursor: 'pointer',
  padding: '0.35rem',
  margin: '0 -0.35rem 0 0',
  flexShrink: 0,
  fontFamily: 'inherit'
};

const revealIconStyle: CSSProperties = {
  fontSize: '1.15rem'
};

const rulesCardStyle: CSSProperties = {
  padding: '0.85rem 0.95rem',
  borderRadius: '12px',
  border: '1px solid rgba(255,255,255,0.08)',
  background: 'rgba(255,255,255,0.03)',
  display: 'flex',
  flexDirection: 'column',
  gap: '0.55rem'
};

const rulesTitleStyle: CSSProperties = {
  fontSize: '0.75rem',
  fontWeight: 700,
  color: 'rgba(255,255,255,0.75)'
};

const rulesListStyle: CSSProperties = {
  display: 'grid',
  gap: '0.45rem'
};

const ruleRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.45rem'
};

const ruleTextStyle: CSSProperties = {
  fontSize: '0.72rem',
  lineHeight: 1.3
};

const feedbackBannerBase: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.55rem',
  padding: '0.85rem 0.95rem',
  borderRadius: '12px',
  fontSize: '0.78rem',
  lineHeight: 1.4
};

const errorBannerStyle: CSSProperties = {
  ...feedbackBannerBase,
  color: '#ffd3d0',
  background: 'rgba(192, 57, 43, 0.16)',
  border: '1px solid rgba(192, 57, 43, 0.28)'
};

const successBannerStyle: CSSProperties = {
  ...feedbackBannerBase,
  color: '#dfffe8',
  background: 'rgba(76, 175, 80, 0.16)',
  border: '1px solid rgba(76, 175, 80, 0.28)'
};

const btnRowStyle: CSSProperties = {
  display: 'flex',
  gap: '0.6rem',
  marginTop: '0.2rem'
};

const primaryBtnStyle: CSSProperties = {
  flex: 1,
  minHeight: '46px',
  border: 'none',
  borderRadius: '12px',
  cursor: 'pointer',
  fontFamily: 'inherit',
  fontSize: '0.88rem',
  fontWeight: 700,
  color: '#ffffff',
  background: 'linear-gradient(135deg, #E98C26 0%, #cf7616 100%)',
  boxShadow: '0 4px 14px rgba(233, 140, 38, 0.25)'
};

const secondaryBtnStyle: CSSProperties = {
  flex: 1,
  minHeight: '46px',
  borderRadius: '12px',
  cursor: 'pointer',
  fontFamily: 'inherit',
  fontSize: '0.88rem',
  fontWeight: 700,
  color: 'rgba(255,255,255,0.82)',
  border: '1px solid rgba(255,255,255,0.1)',
  background: 'rgba(255,255,255,0.05)'
};

const disabledPrimaryBtnStyle: CSSProperties = {
  ...primaryBtnStyle,
  opacity: 0.65,
  cursor: 'not-allowed'
};
